import React, { useState, useEffect, useCallback } from 'react';
import lpApi from '../../../helpers/lpApi';

// ============================================================================
// Big Projects · Audit Log — admin viewer for the Large Projects compliance
// audit trail (who did what to which target, with IP/user-agent).
//
// Consumes besthand-api `listProjectAuditLog(targetType, targetId,
// actorUserId, action, limit)` (returns `auditLogs`) through the `/lp-graphql`
// proxy via `lpApi`. Admin-gated server-side (requireAdmin).
// ============================================================================

const Q_AUDIT = `
  query ($action: String, $targetType: String, $limit: Int) {
    listProjectAuditLog(action: $action, targetType: $targetType, limit: $limit) {
      status errorMessage
      auditLogs {
        id actorUserId actorRole action targetType targetId
        payload ipAddress userAgent createdAt
      }
    }
  }
`;

const S = {
  page: { maxWidth: 1180, margin: '0 auto', padding: 24, fontFamily: "'Inter', -apple-system, sans-serif", color: '#0f1214' },
  h2: { margin: '0 0 4px', fontSize: 24, fontWeight: 800 },
  hint: { color: '#6b7280', fontSize: 14, marginBottom: 20 },
  toolbar: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, flexWrap: 'wrap' },
  input: { padding: '9px 12px', border: '1px solid #d8dde2', borderRadius: 9, fontSize: 14, fontWeight: 600, outline: 'none', minWidth: 160 },
  btn: { background: '#e8232b', color: '#fff', border: 'none', borderRadius: 9, padding: '9px 18px', fontSize: 13, fontWeight: 800, cursor: 'pointer' },
  btnGhost: { background: '#fff', color: '#0f1214', border: '1px solid #e7e9ec', borderRadius: 9, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  card: { border: '1px solid #e7e9ec', borderRadius: 14, padding: 0, overflow: 'hidden', boxShadow: '0 6px 18px rgba(15,18,20,0.03)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, padding: '14px 16px', borderBottom: '1px solid #eef1f3', background: '#fafbfc', whiteSpace: 'nowrap' },
  td: { padding: '12px 16px', borderBottom: '1px solid #f3f5f6', fontSize: 13, verticalAlign: 'top' },
  action: { fontWeight: 800, color: '#0f1214' },
  code: { fontFamily: "'SFMono-Regular', Menlo, monospace", fontSize: 12, color: '#5a6168', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 360, display: 'inline-block' },
  empty: { color: '#6b7280', fontSize: 14, padding: '40px 0', textAlign: 'center' },
  alert: (ok) => ({ padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 16, background: ok ? '#ecfdf5' : '#fef2f2', color: ok ? '#065f46' : '#991b1b' }),
};

const fmtDate = (s) => { if (!s) return ''; try { return new Date(s).toLocaleString(); } catch (e) { return s; } };

function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [action, setAction] = useState('');
  const [targetType, setTargetType] = useState('');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  const firstError = (res) => (res && res.errors && res.errors.length ? res.errors[0].message : 'Something went wrong.');

  const load = useCallback(async (actionArg, targetTypeArg) => {
    setLoading(true);
    setMsg(null);
    try {
      const res = await lpApi(Q_AUDIT, { action: actionArg || null, targetType: targetTypeArg || null, limit: 200 });
      if (res.errors) {
        setMsg({ error: firstError(res) });
      } else {
        const env = res.data && res.data.listProjectAuditLog;
        if (env && env.status === 200) setLogs(env.auditLogs || []);
        else if (env) setMsg({ error: env.errorMessage || `Error ${env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Could not load audit log: ${e.message}` });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load('', ''); }, [load]);

  const apply = () => load(action.trim(), targetType.trim());

  return (
    <div style={S.page}>
      <h2 style={S.h2}>Audit Log</h2>
      <div style={S.hint}>
        Large Projects — compliance audit trail, newest first. Filter by action and/or target type.
      </div>

      <div style={S.toolbar}>
        <input style={S.input} placeholder="Action (e.g. APPROVE_KYB)" value={action} onChange={(e) => setAction(e.target.value)} />
        <input style={S.input} placeholder="Target type (e.g. CONTRACT)" value={targetType} onChange={(e) => setTargetType(e.target.value)} />
        <button style={S.btn} onClick={apply} disabled={loading}>Apply</button>
        <button style={S.btnGhost} onClick={() => { setAction(''); setTargetType(''); load('', ''); }} disabled={loading}>Clear</button>
        <span style={{ color: '#6b7280', fontSize: 13 }}>{logs.length} rows</span>
      </div>

      {msg && <div style={S.alert(!!msg.ok)}>{msg.ok || msg.error}</div>}
      {loading && <div style={S.empty}>Loading…</div>}

      {!loading && (
        logs.length === 0 ? (
          <div style={S.empty}>No audit log entries for this filter.</div>
        ) : (
          <div style={S.card}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>When</th>
                  <th style={S.th}>Actor</th>
                  <th style={S.th}>Action</th>
                  <th style={S.th}>Target</th>
                  <th style={S.th}>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id}>
                    <td style={S.td}>{fmtDate(l.createdAt)}</td>
                    <td style={S.td}>
                      {l.actorUserId || '—'}
                      {l.actorRole ? <div style={{ color: '#9ca3af', fontSize: 12 }}>{l.actorRole}</div> : null}
                    </td>
                    <td style={S.td}><span style={S.action}>{l.action || '—'}</span></td>
                    <td style={S.td}>
                      {l.targetType || '—'}
                      {l.targetId ? <div style={{ color: '#9ca3af', fontSize: 12 }}>#{l.targetId}</div> : null}
                    </td>
                    <td style={S.td}>
                      {l.payload ? <span style={S.code}>{l.payload}</span> : null}
                      {l.ipAddress ? <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 4 }}>{l.ipAddress}</div> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

export default AuditLog;
